import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import {
  ArrowRight,
  Building2,
  Calendar,
  FileText,
  Plus,
  Upload,
  UserRound,
  Users,
} from 'lucide-react';
import { Button, PageHeader, Spinner } from '../components/ui';
import { adminEventService, type AdminEvent } from '../services/adminEventService';
import {
  EVENT_CONTENT_TYPES,
  announceEventWorkspaceUpdated,
  enabledEventContentTypes,
  type EventContentType,
  type EventContentTypeKey,
} from '../utils/eventWorkspace';

const icons: Record<EventContentType['icon'], typeof Calendar> = {
  calendar: Calendar,
  user: UserRound,
  file: FileText,
  building: Building2,
  users: Users,
};

export default function EventContentHubPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { hash } = useLocation();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState<EventContentTypeKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setError('Event ID is missing.');
      setLoading(false);
      return;
    }
    let active = true;
    adminEventService.get(eventId)
      .then((loaded) => {
        if (active) setEvent(loaded);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Could not load event content settings.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => {
    if (!loading && event && hash === '#add-content-type') {
      document.getElementById('add-content-type')?.scrollIntoView({ block: 'start' });
    }
  }, [event, hash, loading]);

  async function enableContentType(key: EventContentTypeKey) {
    if (!event || !eventId || event.effective_role === 'viewer') return;
    setEnabling(key);
    setError(null);
    const appModules = {
      ...event.app_modules,
      [key]: { ...event.app_modules?.[key], enabled: true },
    };
    try {
      const updated = await adminEventService.update(eventId, { app_modules: appModules });
      setEvent(updated);
      announceEventWorkspaceUpdated(updated);
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Could not enable this content type.');
    } finally {
      setEnabling(null);
    }
  }

  if (loading) return <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-gray-500" /></div>;
  if (!event || !eventId) return <div role="alert" className="m-8 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error ?? 'Event not found.'}</div>;

  const enabled = enabledEventContentTypes(event);
  const enabledKeys = new Set(enabled.map(({ key }) => key));
  const optional = EVENT_CONTENT_TYPES.filter(({ key }) => !enabledKeys.has(key));
  const canManage = event.effective_role === 'admin' || event.effective_role === 'manager';

  return (
    <div className="mx-auto max-w-6xl p-8">
      <PageHeader
        title="Content & schedule"
        description={canManage ? `Add, import, and maintain the program for ${event.name}.` : `Explore the program for ${event.name}.`}
        action={canManage && event.effective_content_ingestion_enabled ? (
          <Link to={`/events/${eventId}/content/setup`} className="inline-flex items-center gap-2 rounded-lg bg-sky-500 px-4 py-2 text-sm font-medium text-white hover:bg-sky-400">
            <Upload size={14} /> Import & review
          </Link>
        ) : undefined}
      />

      {error && <p role="alert" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</p>}

      <section aria-label="Enabled content types">
        <div className="mb-4">
          <h2 className="text-base font-semibold text-white">Your event content</h2>
          <p className="mt-1 text-sm text-gray-500">{canManage ? 'Open an editor to add, update, or remove individual records.' : 'Explore this event’s content.'}</p>
        </div>

        {enabled.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-700 bg-gray-900/50 p-8 text-center">
            <p className="text-sm font-medium text-gray-300">No content types enabled yet</p>
            <p className="mt-1 text-xs text-gray-500">Choose what this event needs below. You can change it later.</p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {enabled.map((type) => {
              const Icon = icons[type.icon];
              return (
                <Link key={type.key} to={type.path(eventId)} aria-label={`${canManage ? 'Manage' : 'View'} ${type.label}`} className="group rounded-xl border border-gray-800 bg-gray-900 p-5 transition-colors hover:border-sky-500/30 hover:bg-gray-800/80">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/10 text-sky-400"><Icon size={18} /></div>
                    <ArrowRight size={16} className="mt-2 text-gray-600 group-hover:text-sky-400" />
                  </div>
                  <h3 className="mt-4 text-sm font-semibold text-white">{type.label}</h3>
                  <p className="mt-1 text-xs leading-5 text-gray-400">{type.description}</p>
                </Link>
              );
            })}
          </div>
        )}
      </section>

      {canManage && <section id="add-content-type" aria-label="Add content type" className="mt-8 scroll-mt-6 rounded-2xl border border-gray-800 bg-gray-900/70 p-6">
        <div className="mb-5 flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gray-800 text-gray-400"><Plus size={18} /></div>
          <div>
            <h2 className="text-base font-semibold text-white">Add content type</h2>
            <p className="mt-1 text-sm text-gray-500">Only add the sections this event needs. Enabling one does not create attendee data.</p>
          </div>
        </div>

        {optional.length === 0 ? (
          <p className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-300">All available content types are enabled.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {optional.map((type) => {
              const Icon = icons[type.icon];
              return (
                <div key={type.key} className="flex items-center gap-3 rounded-xl border border-gray-800 bg-gray-950/60 p-4">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-800 text-gray-400"><Icon size={17} /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white">{type.label}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-gray-500">{type.description}</p>
                  </div>
                  <Button size="sm" variant="secondary" loading={enabling === type.key} onClick={() => void enableContentType(type.key)} aria-label={`Add ${type.label}`}>
                    Add
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-5 text-xs text-gray-500">To hide a content type from attendees without deleting its records, use Attendee experience.</p>
      </section>}
    </div>
  );
}
